import { postSSTData } from '../helpers/sst/postSstData';
import { createSSTData } from '../helpers/sst/createSstData';
import { triggerEvent } from '../utilities/utilities';

type RecoEventType = 'frontendOnClick' | 'frontendOnLoad' | 'frontendOnView';

/**
 * Sends a recommendation slider event as r2oElements.
 * Only frontendOnClick is used so far, frontendOnLoad / frontendOnView are prepared for later slider tracking.
 * @param trcId - e.g. 'trc_reco::{location}::{reco-headline}::{reco_rule}::{variation_group_id}'
 * @param eventType - SST event type, defaults to 'frontendOnClick'
 */
const sendRecoTracking = (trcId: string, eventType: RecoEventType = 'frontendOnClick'): void => {
    postSSTData(
        createSSTData({
            jsonPayload: {
                eventType,
                elementType: 'r2oElements',
                r2oElements: [
                    {
                        name: trcId,
                        url: window.location.href,
                    },
                ],
            },
        })
    );
};

const sendTrackingToCloud = (trcId: string): void => {
    // Recommendation sliders are tracked as r2oElements, all other elements as trcElements
    if (trcId.startsWith('trc_reco::')) {
        sendRecoTracking(trcId);

        return;
    }

    postSSTData(
        createSSTData({
            jsonPayload: {
                eventType: 'frontendOnClick',
                elementType: 'trcElements',
                trcElements: [
                    {
                        onClickName: trcId,
                        url: window.location.href,
                    },
                ],
            },
        })
    );
};

const serverSideClickTracking = (): void => {
    const trcElementsClickedOnce: Element[] = [];

    document.addEventListener('customClick', ({ detail }) => {
        const trcElement = (detail.target as Element).closest('[data-trc^="trc_"]');
        const isTrcElementAndDetailDetail = trcElement && detail.detail === 1;

        if (!isTrcElementAndDetailDetail) return;

        const trc = trcElement.getAttribute('data-trc')!;

        // Why detail.detail to prevent double clicks?
        // detail.detail outputs 1 because when the triggerEvent function is called, ev is passed as params, and the
        // params object itself is an event object (MouseEvent from the click event). This means that the detail property
        // of config is set to the params object, which is the original click event. Consequently, ev.detail contains the
        // original click event, and ev.detail.detail returns the value of ev.detail.detail of the click event.

        // Tracking is only triggered once when element is clicked.
        if (trcElement.hasAttribute('data-trc-once') && !trcElementsClickedOnce.includes(trcElement)) {
            trcElementsClickedOnce.push(trcElement);
            sendTrackingToCloud(trc);
            if (window.spsDataLayer?.logControl?.sstLogShow) console.log('sst: Tracking is only triggered once when element is clicked. (once)');

            return;
        }

        // Tracking is triggered with a delay when the element is clicked.
        if (trcElement.hasAttribute('data-trc-delayed')) {
            setTimeout(() => {
                sendTrackingToCloud(trc);
                if (window.spsDataLayer?.logControl?.sstLogShow) console.log('sst: Tracking is triggered with a delay when the element is clicked. (delayed)');
            }, 500);

            return;
        }

        // Tracking is always triggered when an element is clicked. (default)
        if (!trcElementsClickedOnce.includes(trcElement)) {
            sendTrackingToCloud(trc);
            if (window.spsDataLayer?.logControl?.sstLogShow) console.log('sst: Tracking is always triggered when an element is clicked. (default)');
        }
    });
};

/**
 * Programmatically triggers a server-side tracking (SST) event.
 * This function bypasses the need for a physical DOM element by creating
 * a "mock" element in memory, allowing tracking to be fired from
 * logic-heavy environments like iframes or dynamic step-flows.
 * @param trcId - The tracking ID (e.g., 'trc_checkout::review::payment::paypal') that identifies the specific user interaction to be tracked.
 */
const trackCustomClickByTrcId = (trcId: string): void => {
    const mockElement = document.createElement('div');
    mockElement.setAttribute('data-trc', trcId);

    triggerEvent('customClick', document, {
        target: mockElement,
        detail: 1
    });
};

const methods = { sendTrackingToCloud, sendRecoTracking };

// processInclude() in main.js calls every exported function, incl. trackCustomClickByTrcId() without an id (ignored by the listener)
export { methods, serverSideClickTracking, trackCustomClickByTrcId };
